import { useParams, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, Clock, FileText, Phone, Globe, Award, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.1 } }
};

// Hero images by category
const CATEGORY_IMAGES = {
  'canada': 'https://images.unsplash.com/photo-1618229234536-dd459d33ad73?w=1920',
  'australia': 'https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?w=1920',
  'usa': 'https://images.unsplash.com/photo-1485738422979-f5c462d49f74?w=1920',
  'uk': 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1920',
  'germany': 'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=1920',
  'europe': 'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=1920',
  'uae': 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920',
  'new-zealand': 'https://images.unsplash.com/photo-1507699622108-4be3abd695ad?w=1920',
  'saudi-arabia': 'https://images.unsplash.com/photo-1586724237569-f3d0c1dee8c6?w=1920',
  'default': 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1920'
};

const CMSPage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BACKEND_URL}/api/pages/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setPageData(data);
          // Update page title
          document.title = `${data.meta_title || data.title} | Zafco Human Resource Management`;
        } else if (response.status === 404) {
          setError('Page not found');
        } else {
          setError('Failed to load page');
        }
      } catch (err) {
        setError('Connection error');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPage();
    }

    return () => {
      document.title = 'Zafco HR | Your Gateway to Global Careers';
    };
  }, [slug]);

  const openWhatsApp = () => {
    const message = `Hello Zafco, I need help with ${pageData?.title || 'overseas recruitment'}`;
    window.open(`https://wa.me/919958848998?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#C5A028] mx-auto mb-4" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4 font-syne">Page Not Found</h1>
          <p className="text-slate-400 mb-6">This page doesn't exist or has been moved.</p>
          <Button onClick={openWhatsApp} className="bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold">
            Contact Us
          </Button>
        </div>
      </div>
    );
  }

  const heroImage = CATEGORY_IMAGES[pageData.category] || CATEGORY_IMAGES['default'];
  const pathParts = location.pathname.split('/').filter(Boolean);
  const breadcrumbs = pathParts.map((part, index) => ({
    label: part.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    path: index < pathParts.length - 1 ? '/' + pathParts.slice(0, index + 1).join('/') : undefined
  }));

  return (
    <div className="bg-slate-950" data-testid="cms-page">
      <PageHeader 
        title={pageData.title}
        subtitle={pageData.meta_description}
        breadcrumbs={breadcrumbs}
        backgroundImage={heroImage}
      />

      <section className="py-20 bg-slate-950">
        <div className="container max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-16">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="lg:col-span-2"
            >
              <div className="prose prose-invert prose-lg max-w-none 
                prose-headings:font-syne prose-headings:text-white prose-headings:font-bold
                prose-h1:text-3xl prose-h1:md:text-4xl prose-h1:mb-6 prose-h1:mt-8
                prose-h2:text-2xl prose-h2:md:text-3xl prose-h2:mb-4 prose-h2:mt-8 prose-h2:text-[#C5A028]
                prose-h3:text-xl prose-h3:mb-3 prose-h3:mt-6
                prose-p:text-slate-400 prose-p:leading-relaxed prose-p:mb-4
                prose-ul:text-slate-400 prose-ul:my-4
                prose-ol:text-slate-400 prose-ol:my-4
                prose-li:mb-2
                prose-strong:text-white
                prose-a:text-[#C5A028] prose-a:no-underline hover:prose-a:underline
                prose-blockquote:border-l-[#C5A028] prose-blockquote:bg-slate-900/50 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:rounded-r-lg
                prose-table:border-collapse prose-th:bg-slate-800 prose-th:text-white prose-th:p-3 prose-th:border prose-th:border-white/10
                prose-td:p-3 prose-td:border prose-td:border-white/10 prose-td:text-slate-400
              ">
                <ReactMarkdown>{pageData.content}</ReactMarkdown>
              </div>

              <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-4 mt-12">
                <div className="bg-slate-900/50 border border-white/5 rounded-xl p-4">
                  <Clock className="w-6 h-6 text-[#C5A028] mb-2" />
                  <p className="text-2xl font-bold text-white font-syne">6-18 Mo</p>
                  <p className="text-sm text-slate-400">Processing Time</p>
                </div>
                <div className="bg-slate-900/50 border border-white/5 rounded-xl p-4">
                  <Award className="w-6 h-6 text-[#C5A028] mb-2" />
                  <p className="text-2xl font-bold text-white font-syne">98%</p>
                  <p className="text-sm text-slate-400">Success Rate</p>
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="mt-8">
                <Button 
                  onClick={openWhatsApp}
                  className="bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold px-8 py-6 rounded-full"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Get Free Assessment
                </Button>
              </motion.div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInRight}
              className="glass-premium rounded-2xl p-8 sticky top-24 h-fit"
            >
              <h3 className="text-xl font-bold text-white font-syne mb-6">Free Consultation</h3>
              <ConsultationForm variant="dark" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
        
        <div className="container max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center"
          >
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-syne mb-6">
              Ready to Start Your Journey?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
              Get your free assessment and personalized guidance from Zafco's immigration experts today.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button 
                onClick={openWhatsApp}
                size="lg"
                className="bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold px-10 py-7 text-lg rounded-full btn-gold-glow"
              >
                <Phone className="w-6 h-6 mr-3" />
                Get Free Assessment
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CMSPage;
